/**
 * feat-307-redesign-tenant-mgmt — the location pane
 * (/tenant_management/:tenantId/locations/:locationId).
 *
 * Per design/NOTES.md §6 column "1 · Tree":
 *   breadcrumb (tenant → location; how a Manager, who has no sidebar, reaches
 *   the tenant-level surfaces they can see today, F5)
 *   L1 name / created / timezone / auth · L12 go-live
 *   L2 "Servers: n | Managers: n | Shift leads: n"
 *   L3 drip summary (Admin-only, fail-closed — the caller passes the gate)
 *   L4 Add server · L5 Add manager · L6 Edit location · L7 Consent evidence
 *   L8 Crew table (Edit, XP History, Shifts ↗) · L9 Managers
 *   L9b Shift leads (feat-321; Deactivate is Admin-only, fail-closed) · L10 Manager shifts
 *   L11 server shift scores inside an expanded manager shift
 *
 * Layout only: every gate is computed by TenantDashboard and every action calls
 * back into TenantDashboard's existing handlers. Labels "Consent evidence",
 * "XP History" and "Shifts" keep their shipped wording (spec D9).
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ExternalLink, FileText, Plus, Search, Settings } from './icons';
import { Location, Manager, Server, Tenant } from '../../../types/tenant';
import { RoleNameLookup, ServerRolesCell, roleDisplayName, serverRolesFromApi } from '../serverRoles';
import { DRIP_SUMMARY_PREFIX, dripSummaryValue } from '../drip/dripCopy';
import { effectiveLocationDrip } from '../drip/dripSettings';
import { cls, fmtDate } from './styles';
import { tenantPath } from './routes';
import { ManagerShiftRecord, ServerShiftsState } from './types';

export interface LocationPaneProps {
  tenant: Tenant;
  location: Location;
  detailsLoaded: boolean;
  detailsLoading: boolean;
  /** Fail-closed Admin gate for L3. */
  showDripSummary: boolean;
  canCreateServer: boolean;
  onAddServer: () => void;
  canCreateManager: boolean;
  onAddManager: () => void;
  canEditLocation: boolean;
  onEditLocation: () => void;
  generatingReport: boolean;
  onConsentEvidence: () => void;
  onEditServer: (server: Server) => void;
  onXpHistory: (server: Server) => void;
  formatPhone: (phone: string) => string;
  canDeleteManager: (managerId: string) => boolean;
  onDeactivateManager: (manager: Manager) => void;
  /**
   * feat-321 — Admin-only, FAIL-CLOSED gate for the shift-lead Deactivate link.
   * Deliberately NOT `canDeleteManager`: that is true for Corporate (and for
   * everyone in the dev fallback), but `DELETE /api/managers/{id}` on a
   * `shift_lead` row is admin-only (feat-192 default-deny), so a Corporate user
   * would see a link that 403s. Absent ⇒ hidden.
   */
  canDeactivateShiftLead?: boolean;
  onDeactivateShiftLead?: (shiftLead: Manager) => void;
  managerShifts: ManagerShiftRecord[] | undefined;
  expandedManagerShifts: Record<string, boolean>;
  serverShiftsForManager: Record<string, ServerShiftsState>;
  onToggleManagerShift: (ms: ManagerShiftRecord) => void;
  onAddManagerShift: () => void;
  onEditManagerShift: (ms: ManagerShiftRecord) => void;
  onDeleteManagerShift: (ms: ManagerShiftRecord) => void;
  /**
   * feat-308: the tenant's role_key → display_name map for the Crew table's
   * Role column and filter. Absent/empty (loading, failed) ⇒ raw keys.
   */
  roleNames?: RoleNameLookup | null;
}

const SECTION_IDS = {
  crew: 'location-crew',
  managers: 'location-managers',
  shiftLeads: 'location-shift-leads',
  shifts: 'location-manager-shifts',
};

export default function LocationPane(props: LocationPaneProps) {
  const {
    tenant,
    location,
    detailsLoaded,
    detailsLoading,
    showDripSummary,
    canCreateServer,
    onAddServer,
    canCreateManager,
    onAddManager,
    canEditLocation,
    onEditLocation,
    generatingReport,
    onConsentEvidence,
    onEditServer,
    onXpHistory,
    formatPhone,
    canDeleteManager,
    onDeactivateManager,
    canDeactivateShiftLead = false,
    onDeactivateShiftLead,
    managerShifts,
    expandedManagerShifts,
    serverShiftsForManager,
    onToggleManagerShift,
    onAddManagerShift,
    onEditManagerShift,
    onDeleteManagerShift,
    roleNames,
  } = props;

  const [crewQuery, setCrewQuery] = useState('');
  const crew = useMemo(() => {
    const q = crewQuery.trim().toLowerCase();
    if (!q) return location.servers;
    const qDigits = q.replace(/\D/g, '');
    return location.servers.filter((s) => {
      const keys = s.roles ?? serverRolesFromApi(undefined, s.defaultRole);
      // feat-308: a role matches by its role_key OR its display name.
      const roles = `${keys.join(' ')}\n${keys.map((k) => roleDisplayName(k, roleNames)).join(' ')}`;
      return (
        s.name.toLowerCase().includes(q) ||
        roles.toLowerCase().includes(q) ||
        (qDigits.length > 0 && (s.phone || '').replace(/\D/g, '').includes(qDigits))
      );
    });
  }, [location.servers, crewQuery, roleNames]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const eff = effectiveLocationDrip(location, tenant);
  const dripValue = dripSummaryValue(eff.enabled, eff.rate, eff.overridden);
  const countOrDash = (n: number) => (detailsLoaded ? n : '—');
  // feat-321 D2: a failed shift-leads fetch is "unknown", never a false 0 —
  // header shows "—" and the tab count is blank, same as while loading.
  const shiftLeads = location.shiftLeads ?? [];
  const shiftLeadsKnown = detailsLoaded && !location.shiftLeadsError;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl space-y-5" data-testid={`location-pane-${location.id}`}>
      <nav className="text-sm text-inkMuted flex items-center gap-1 flex-wrap" aria-label="Breadcrumb">
        <Link className="text-inkMuted no-underline hover:text-coral font-semibold" to={tenantPath(tenant.tenantId)} data-testid="breadcrumb-tenant">
          {tenant.tenantName}
        </Link>
        <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
        <span className="text-ink font-semibold" aria-current="page">
          {location.name}
        </span>
      </nav>

      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-[16rem] flex-1">
          <h2 className="font-display text-2xl lg:text-3xl font-semibold leading-tight text-ink break-words">
            {location.name}
          </h2>
          <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm text-inkMuted" data-testid="location-meta">
            <span>Created: {new Date(location.createdAt).toLocaleDateString()}</span>
            <span>Timezone: {location.timezone}</span>
            <span>Auth: {location.authMethod === 'pin' ? 'PIN' : 'Phone OTP'}</span>
            <span>Go-live: {location.bespokeGoLiveDate || 'Not yet live'}</span>
            <span>
              Servers: {countOrDash(location.servers.length)} | Managers: {countOrDash(location.managers.length)}
              {' | Shift leads: '}
              {shiftLeadsKnown ? shiftLeads.length : '—'}
            </span>
          </div>
          {/* feat-303 — EFFECTIVE drip setting, admin only (fail-closed gate from the caller). */}
          {showDripSummary && (
            <p className="text-sm text-inkMuted mt-1" data-testid={`location-drip-summary-${location.id}`}>
              {DRIP_SUMMARY_PREFIX}
              {eff.overridden ? <span className="text-ink font-medium">{dripValue}</span> : dripValue}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="location-actions">
          {canCreateServer && (
            <button type="button" onClick={onAddServer} className={cls.btnPrimary}>
              <Plus className="w-4 h-4" aria-hidden="true" />
              Add server
            </button>
          )}
          {canCreateManager && (
            <button type="button" onClick={onAddManager} className={cls.btnSecondary}>
              <Plus className="w-4 h-4" aria-hidden="true" />
              Add manager
            </button>
          )}
          {canEditLocation && (
            <button type="button" onClick={onEditLocation} className={cls.btnSecondary}>
              <Settings className="w-4 h-4" aria-hidden="true" />
              Edit location
            </button>
          )}
          {/* feat-022-consent-evidence: location-level consent evidence PDF. */}
          {canEditLocation && (
            <button
              type="button"
              onClick={onConsentEvidence}
              disabled={generatingReport}
              className={cls.btnSecondary}
              title="Download a consent evidence PDF for this location"
            >
              <FileText className="w-4 h-4" aria-hidden="true" />
              {generatingReport ? 'Generating…' : 'Consent evidence'}
            </button>
          )}
        </div>
      </div>

      <div className="sticky top-0 z-10 bg-cream/95 backdrop-blur py-2 flex gap-1 text-sm font-semibold border-b border-softBorder overflow-x-auto">
        <button type="button" onClick={() => scrollTo(SECTION_IDS.crew)} className="px-3 py-1 rounded-lg bg-navActiveBg text-navActive whitespace-nowrap">
          Crew {detailsLoaded ? location.servers.length : ''}
        </button>
        <button type="button" onClick={() => scrollTo(SECTION_IDS.managers)} className="px-3 py-1 rounded-lg hover:bg-cream-dark whitespace-nowrap">
          Managers {detailsLoaded ? location.managers.length : ''}
        </button>
        <button type="button" onClick={() => scrollTo(SECTION_IDS.shiftLeads)} className="px-3 py-1 rounded-lg hover:bg-cream-dark whitespace-nowrap">
          Shift leads {shiftLeadsKnown ? shiftLeads.length : ''}
        </button>
        <button type="button" onClick={() => scrollTo(SECTION_IDS.shifts)} className="px-3 py-1 rounded-lg hover:bg-cream-dark whitespace-nowrap">
          Manager shifts {managerShifts ? managerShifts.length : ''}
        </button>
      </div>

      {/* L8 — Crew (servers) */}
      <section id={SECTION_IDS.crew} className={`${cls.card} p-4`} aria-labelledby="crew-heading">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h3 id="crew-heading" className={`${cls.h2} !text-base`}>
            Crew
          </h3>
          {detailsLoaded && location.servers.length > 0 && (
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-inkMuted" aria-hidden="true" />
              <input
                type="search"
                value={crewQuery}
                onChange={(e) => setCrewQuery(e.target.value)}
                className={`${cls.input} pl-8 !py-1.5`}
                placeholder={`Filter ${location.servers.length} crew by name, role or phone`}
                aria-label="Filter crew"
              />
            </div>
          )}
        </div>
        {!detailsLoaded ? (
          <p className={cls.muted}>{detailsLoading ? 'Loading...' : ''}</p>
        ) : location.servers.length === 0 ? (
          <p className={cls.muted}>No servers found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className={cls.th}>Name</th>
                  <th className={cls.th}>Role</th>
                  <th className={cls.th}>Created</th>
                  <th className={cls.th}>Phone</th>
                  {location.authMethod === 'pin' && <th className={cls.th}>PIN</th>}
                  <th className={`${cls.th} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {crew.map((server) => (
                  <tr key={server.id} className="border-t border-softBorder">
                    <td className={`${cls.td} font-semibold`}>{server.name}</td>
                    <ServerRolesCell roles={server.roles ?? serverRolesFromApi(undefined, server.defaultRole)} roleNames={roleNames} />
                    <td className={`${cls.td} text-inkMuted whitespace-nowrap`}>
                      {new Date(server.createdAt).toLocaleDateString()}
                    </td>
                    <td className={`${cls.td} whitespace-nowrap`}>{server.phone ? formatPhone(server.phone) : 'N/A'}</td>
                    {location.authMethod === 'pin' && (
                      <td className={`${cls.td} font-mono`}>{server.pin || '—'}</td>
                    )}
                    <td className={`${cls.td} text-right`}>
                      <div className="flex gap-3 justify-end whitespace-nowrap">
                        <button type="button" onClick={() => onEditServer(server)} className={cls.act}>
                          Edit
                        </button>
                        <button type="button" onClick={() => onXpHistory(server)} className={cls.act}>
                          XP History
                        </button>
                        <Link
                          to={`/restaurant/shifts?serverId=${server.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${cls.act} no-underline inline-flex items-center gap-0.5`}
                        >
                          Shifts
                          <ExternalLink className="w-3 h-3" aria-hidden="true" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
                {crew.length === 0 && (
                  <tr>
                    <td colSpan={6} className={`${cls.td} text-inkMuted`}>
                      No crew match.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* L9 — Managers */}
      <section id={SECTION_IDS.managers} className={`${cls.card} p-4`} aria-labelledby="managers-heading">
        <h3 id="managers-heading" className={`${cls.h2} !text-base mb-3`}>
          Managers
        </h3>
        {!detailsLoaded ? (
          <p className={cls.muted}>{detailsLoading ? 'Loading...' : ''}</p>
        ) : location.managers.length === 0 ? (
          <p className={cls.muted}>No managers found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className={cls.th}>Name</th>
                  <th className={cls.th}>Email</th>
                  <th className={cls.th}>Created</th>
                  <th className={`${cls.th} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {location.managers.map((manager) => (
                  <tr key={manager.id} className="border-t border-softBorder">
                    <td className={`${cls.td} font-semibold`}>
                      {manager.firstName} {manager.lastName}
                    </td>
                    <td className={cls.td}>{manager.email}</td>
                    <td className={`${cls.td} text-inkMuted`}>{fmtDate(manager.createdAt)}</td>
                    <td className={`${cls.td} text-right`}>
                      {canDeleteManager(manager.id) && (
                        <button
                          type="button"
                          onClick={() => onDeactivateManager(manager)}
                          title="Revoke login and remove from manager list"
                          className={cls.actDanger}
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* L9b — Shift leads (feat-321). A line-for-line copy of the Managers card;
          only the strings, the data and the Deactivate gate differ (design D1–D4). */}
      <section id={SECTION_IDS.shiftLeads} className={`${cls.card} p-4`} aria-labelledby="shift-leads-heading">
        <h3 id="shift-leads-heading" className={`${cls.h2} !text-base mb-3`}>
          Shift leads
        </h3>
        {!detailsLoaded ? (
          <p className={cls.muted}>{detailsLoading ? 'Loading...' : ''}</p>
        ) : location.shiftLeadsError ? (
          <p className={cls.muted}>Couldn't load shift leads.</p>
        ) : shiftLeads.length === 0 ? (
          <p className={cls.muted}>No shift leads at this location.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className={cls.th}>Name</th>
                  <th className={cls.th}>Email</th>
                  <th className={cls.th}>Created</th>
                  <th className={`${cls.th} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {shiftLeads.map((lead) => (
                  <tr key={lead.id} className="border-t border-softBorder">
                    <td className={`${cls.td} font-semibold`}>
                      {lead.firstName} {lead.lastName}
                    </td>
                    <td className={cls.td}>{lead.email}</td>
                    <td className={`${cls.td} text-inkMuted`}>{fmtDate(lead.createdAt)}</td>
                    <td className={`${cls.td} text-right`}>
                      {canDeactivateShiftLead && onDeactivateShiftLead && (
                        <button
                          type="button"
                          onClick={() => onDeactivateShiftLead(lead)}
                          title="Stop this location's exception report emails and remove from the shift lead list"
                          className={cls.actDanger}
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* L10 / L11 — Manager shifts, server shift scores under an expanded shift */}
      <section id={SECTION_IDS.shifts} className={`${cls.card} p-4`} aria-labelledby="manager-shifts-heading">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <h3 id="manager-shifts-heading" className={`${cls.h2} !text-base`}>
            Manager shifts
          </h3>
          <button type="button" onClick={onAddManagerShift} className={cls.btnSecondary}>
            <Plus className="w-4 h-4" aria-hidden="true" />
            Add manager shift
          </button>
        </div>
        {!managerShifts ? (
          <p className={cls.muted}>Loading...</p>
        ) : managerShifts.length === 0 ? (
          <p className={cls.muted}>No manager shifts found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className={cls.th}>Start</th>
                  <th className={cls.th}>End</th>
                  <th className={cls.th}>Manager</th>
                  <th className={cls.th}>Servers</th>
                  <th className={cls.th}>Type</th>
                  <th className={`${cls.th} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {managerShifts.map((ms) => {
                  const open = !!expandedManagerShifts[ms.id];
                  const ss = serverShiftsForManager[ms.id];
                  return (
                    <React.Fragment key={ms.id}>
                      <tr
                        className={`border-t border-softBorder cursor-pointer hover:bg-cream ${open ? 'bg-cream-dark' : ''}`}
                        onClick={() => onToggleManagerShift(ms)}
                        aria-expanded={open}
                      >
                        <td className={`${cls.td} whitespace-nowrap`}>
                          <span className="inline-block w-4 mr-1 text-inkMuted" aria-hidden="true">
                            {open ? '▼' : '▶'}
                          </span>
                          {new Date(ms.start_time).toLocaleString()}
                        </td>
                        <td className={`${cls.td} whitespace-nowrap`}>
                          {ms.end_time ? new Date(ms.end_time).toLocaleString() : 'N/A'}
                        </td>
                        <td className={cls.td}>{ms.manager_name}</td>
                        <td className={cls.td} title={ms.server_names.join(', ')}>
                          {ms.server_names.length} server{ms.server_names.length !== 1 ? 's' : ''}
                        </td>
                        <td className={cls.td}>{ms.shift_type || 'N/A'}</td>
                        <td className={`${cls.td} text-right`}>
                          <div className="flex gap-3 justify-end">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditManagerShift(ms);
                              }}
                              className={cls.act}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteManagerShift(ms);
                              }}
                              className={cls.actDanger}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={6} className="bg-cream-dark px-6 py-3">
                            {ss?.loading ? (
                              <p className={cls.muted}>Loading server shifts...</p>
                            ) : !ss?.shifts.length ? (
                              <p className={cls.muted}>No server shifts found in this time window.</p>
                            ) : (
                              <div className="overflow-x-auto">
                                <p className={`${cls.eyebrow} mb-2`}>Server Shifts ({ss.shifts.length})</p>
                                <table className="min-w-full bg-white rounded-lg border border-softBorder">
                                  <thead>
                                    <tr>
                                      <th className={cls.th}>Server</th>
                                      <th className={cls.th}>Score</th>
                                      <th className={cls.th}>Greetings</th>
                                      <th className={cls.th}>Premium Menu</th>
                                      <th className={cls.th}>Item Mention</th>
                                      <th className={cls.th}>Item Desc.</th>
                                      <th className={cls.th}>Drinks</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {ss.shifts.map((s) => (
                                      <tr key={s.id} className="border-t border-softBorder">
                                        <td className={`${cls.td} text-xs`}>{s.name}</td>
                                        <td className={`${cls.td} text-xs font-semibold`}>{s.score}%</td>
                                        <td className={`${cls.td} text-xs`}>{s.greetings}</td>
                                        <td className={`${cls.td} text-xs`}>{s.premium_menu}%</td>
                                        <td className={`${cls.td} text-xs`}>{s.item_mention}%</td>
                                        <td className={`${cls.td} text-xs`}>{s.item_description}%</td>
                                        <td className={`${cls.td} text-xs`}>{s.drink_promotions}%</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
